import React, { useRef, useEffect, useMemo } from "react";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openSettings } from "../../../../State/actions";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PRIMARY_USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";

const ButtonSettings = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig } = useDimensions();
  const { settingsActive } = useSelector((state) => state.userInterface);

  const {
    hoverContainerRef,
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
  } = useButtonHover();
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const texture = getUITexture(
    settingsActive ? "icon_settings_active" : "icon_settings",
  );

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Right of center (Paytable is center, Settings is right)
  const buttonPosition = useMemo(
    () => ({
      x: toolbar.width / 2 + buttonConfig.width * 3.0,
      y: toolbar.height / 2,
    }),
    [toolbar.width, toolbar.height, buttonConfig.width],
  );

  const handleClick = () => {
    if (isProcessingClickRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;
    soundManager.play(SOUND_NAME.button_click);
    triggerPress();

    if (!settingsActive) {
      dispatch(openSettings());
    }

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Container
      name="menu_button_settings"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_settings_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={settingsActive ? t("close", { ns: "common" }) : t("settings_title", { ns: "common" })}
          accessibleHint={settingsActive ? t("close", { ns: "common" }) : t("settings_title", { ns: "common" })}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonSettings;
